import _ from "lodash";
import { useMappedState } from "redux-react-hook";
import moment from "moment-timezone";
import React from "react";

import {
  IOrder,
  IOrderStatus,
  OrderPaymentStatus
} from "src/core/orders/api/orders.api";
import { IStore } from "src/core/shared/store";
import { useOrderCutoffTime } from "src/core/orders/hooks/validation";
import { useDrop } from "src/core/drops/hooks";

/**
 * This hook determines if a customer can pay for an order or not
 * based on their settings as
 * @param order
 * @returns
 */
export const useCanPayForOrder = (order: IOrder | null): boolean => {
  const cutoffTime = useOrderCutoffTime();
  const [ { drop } ] = useDrop();

  const {
    orderStatuses, installedModules, preauth, currentRole
  } = useMappedState(({ global }: IStore) => ({
    currentRole: global.role,
    orderStatuses: (global.settings && global.settings.orderStatuses) || [] as IOrderStatus[],
    installedModules: global.settings ? global.settings.installedModules : [],
    preauth: global.settings?.payment?.preauthorizationsEnabled
  }));

  return React.useMemo(() => {
    if (!order) {
      return false;
    }

    if (!drop?.groupedSettings.require_payment_method?.value.required) {
      return false;
    }

    if (order.paymentStatus && [
      OrderPaymentStatus.UNCAPTURED,
      OrderPaymentStatus.CAPTURED,
      OrderPaymentStatus.PARTIAL_REFUNDED,
      OrderPaymentStatus.REFUNDED,
      OrderPaymentStatus.REFUND_FAILED
    ].includes(order.paymentStatus)) {
      return false;
    }

    if (currentRole?.type === "customer") {
      const payableStatuses = _.map(_.filter(orderStatuses, ordStatus => {
        return ordStatus.settings.group === "PROVISIONAL";
      }), "id");

      const orderIsPayable = payableStatuses.includes(order.orderStatusId);

      if (installedModules.includes("payment") && orderIsPayable) {
        const cutoff = cutoffTime(order);

        // allow payment within 6 hours of cutoff if preauth
        if (preauth && cutoff && moment.utc(cutoff).diff(moment.utc(), "hours") < 6) {
          return true;
        }

        // otherwise allow payment any time if not preauth
        if (!preauth) {
          return true;
        }
      }
    }

    return false;
  }, [
    currentRole?.type,
    cutoffTime,
    drop?.groupedSettings.require_payment_method?.value.required,
    installedModules,
    order,
    orderStatuses,
    preauth
  ]);
};
